import React from 'react';
import { useTranslation } from 'react-i18next';
import './IvrStatsTableWidget.css';

/**
 * IvrStatsTableWidget — tableau récap des menus IVR (KPI ivr-statistics). Lignes par menu,
 * groupées par flow : entrées, abandon%, invalide%, timeout%, replays moyens, durée moyenne,
 * action principale (max de la map `actions`).
 */
const IvrStatsTableWidget = ({ data, config }) => {
    const { t } = useTranslation();

    if (!Array.isArray(data) || data.length === 0) {
        return null;
    }

    const COLS = 8;

    const fmtInt = (v) => (v === null || v === undefined ? '-' : Number(v).toLocaleString());
    const fmtPct = (v) => {
        if (v === null || v === undefined) return '-';
        const n = Number(v);
        return `${n % 1 === 0 ? n.toFixed(0) : n.toFixed(1)}%`;
    };
    const fmtNum = (v) => {
        if (v === null || v === undefined) return '-';
        const n = Number(v);
        return n % 1 === 0 ? n.toFixed(0) : n.toFixed(2);
    };
    const fmtDur = (s) => {
        if (s === null || s === undefined) return '-';
        const total = Math.max(0, Math.round(Number(s)));
        const m = Math.floor(total / 60);
        const sec = total % 60;
        return m > 0 ? `${m}:${String(sec).padStart(2, '0')}` : `${sec}s`;
    };
    const mainAction = (actions) => {
        const entries = Object.entries(actions || {});
        if (entries.length === 0) return '-';
        entries.sort((a, b) => Number(b[1]) - Number(a[1]));
        return entries[0][0];
    };

    // Groupement par flow.
    const groups = [];
    const idx = {};
    data.forEach((r) => {
        const k = r.flowName || '—';
        if (idx[k] === undefined) { idx[k] = groups.length; groups.push({ flowName: k, rows: [] }); }
        groups[idx[k]].rows.push(r);
    });

    return (
        <div className="ivr-st-widget">
            <div className="ivr-st-table-container">
                <table className="ivr-st-table">
                    <thead>
                        <tr>
                            <th className="col-menu">{t('IVR_MENU')}</th>
                            <th>{t('IVR_ENTRIES')}</th>
                            <th>{t('IVR_ABANDON_PCT')}</th>
                            <th>{t('IVR_INVALID_PCT')}</th>
                            <th>{t('IVR_TIMEOUT_PCT')}</th>
                            <th>{t('IVR_AVG_REPLAYS')}</th>
                            <th>{t('IVR_AVG_DURATION')}</th>
                            <th>{t('IVR_MAIN_ACTION')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {groups.map((g, gi) => (
                            <React.Fragment key={gi}>
                                <tr className="flow-group">
                                    <td colSpan={COLS}>{g.flowName}</td>
                                </tr>
                                {g.rows.map((r, ri) => (
                                    <tr key={`${gi}-${ri}`}>
                                        <td className="col-menu-val">{r.ivrName ?? '-'}</td>
                                        <td className="col-number">{fmtInt(r.entries)}</td>
                                        <td className="col-pct-val">{fmtPct(r.abandonPct)}</td>
                                        <td className="col-pct-val">{fmtPct(r.invalidPct)}</td>
                                        <td className="col-pct-val">{fmtPct(r.timeoutPct)}</td>
                                        <td className="col-number">{fmtNum(r.avgReplays)}</td>
                                        <td className="col-number">{fmtDur(r.avgDurationSec)}</td>
                                        <td className="col-action">{mainAction(r.actions)}</td>
                                    </tr>
                                ))}
                            </React.Fragment>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default IvrStatsTableWidget;
